<script lang="ts" setup>
import { computed } from 'vue';

const VARIANTS = [
  "default",
  "link",
  "naked",
  "invert",
  "add",
  "create",
  "delete",
  "edit",
  "view",
  "outline",
  "deactivate",
  "warning",
  "danger",
  "stale",
  "cancel"
] as const;
const SIZES = ["xs", "sm", "default", "lg", "xl", "xxl", "full"] as const;
const ROUNDED = ["xs", "sm", "default", "lg", "xl", "xxl", "full"] as const;

export type ButtonVariant = typeof VARIANTS[number];
export type ButtonSize = typeof SIZES[number];
export type ButtonRounded = typeof ROUNDED[number];

interface ButtonProps {
  loading?: boolean;
  disabled?: boolean;
  loadingMsg?: string;
  type?: "button" | "submit" | "reset";
  testId?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  rounded?: ButtonRounded;
}

const variantMap: Record<ButtonVariant, string[]> = {
  default: ['button'],
  link: ['naked', 'link'],
  naked: ['naked'],
  invert: ['invert'],
  add: ['add'],
  create: ['create'],
  delete: ['delete'],
  edit: ['edit'],
  view: ['view'],
  outline: ['outline'],
  deactivate: ['deactivate'],
  warning: ['warning'],
  danger: ['danger'],
  stale: ['stale'],
  cancel: ['cancel'],
};

const sizeMap: Record<ButtonSize, string> = {
  xs: "xtra-small",
  sm: "small",
  default: "",
  lg: "large",
  xl: "xtra-large",
  xxl: "xxtra-large",
  full: ""
};

const roundedMap: Record<ButtonRounded, string> = {
  xs: "rounded-xs",
  sm: "rounded-sm",
  default: "",
  lg: "rounded-lg",
  xl: "rounded-xl",
  xxl: "rounded-xxl",
  full: "rounded-full"
};

const props = withDefaults(defineProps<ButtonProps>(), {
  loading: false,
  disabled: false,
  loadingMsg: "Loading...",
  type: "button",
  testId: "button",
  variant: "default",
  size: "default",
  rounded: "default",
});

const buttonClasses = computed(() => {
  const classes: string[] = [];

  // Add variant classes from the map
  classes.push(...variantMap[props.variant]);

  // Add size class
  const sizeClass = sizeMap[props.size];
  if (sizeClass) {
    classes.push(sizeClass);
  }

  // Add rounded class
  const roundedClass = roundedMap[props.rounded];
  if (roundedClass) {
    classes.push(roundedClass);
  }

  return classes.filter(Boolean).join(' ');
});
</script>

<template>
  <button
    :type="type"
    :class="[buttonClasses, { disabled: loading || disabled }]"
    :disabled="disabled || loading"
    :data-testid="testId"
  >
    <div class="position-relative flex-center w-100">
      <span class="flex-center w-100" :style="{ visibility: loading ? 'hidden' : 'visible' }">
        <slot />
      </span>
      <span v-if="loading" class="position-absolute flex-center" style="inset: 0;">
        <output class="spinner-border spinner-border-sm" :aria-label="loadingMsg">
          <span class="visually-hidden">{{ loadingMsg }}</span>
        </output>
      </span>
    </div>
  </button>
</template>

<style scoped>
button {
  width: 100%;
  padding: 0.75rem;
  font-size: var(--font-base);
  font-weight: 500;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  border-radius: 5px;
  text-transform: capitalize;
  transition: 0.2s;
  -webkit-transition: 0.2s;
  -o-transition: 0.2s;
}

button:active {
  -webkit-transform: scale(0.99);
  -ms-transform: scale(0.99);
  transform: scale(0.99);
  opacity: 0.9;
  -webkit-transition: opacity 100ms;
  -o-transition: opacity 100ms;
  transition: opacity 100ms;
}

@media screen and (min-width: 1200px) {
  button:hover {
    -webkit-transform: translateY(-2px);
    -ms-transform: translateY(-2px);
    transform: translateY(-2px);
    -webkit-box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  button:active {
    -webkit-transform: translateY(-1px);
    -ms-transform: translateY(-1px);
    transform: translateY(-1px);
    -webkit-box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }

  button[disabled]:hover,
  button[disabled]:active {
    -webkit-transform: translateY(0);
    -ms-transform: translateY(0);
    transform: translateY(0);
    -webkit-box-shadow: none;
    box-shadow: none;
  }

  .naked:hover {
    -webkit-transform: translateY(-1.5px);
    -ms-transform: translateY(-1.5px);
    transform: translateY(-1.5px);
    -webkit-box-shadow: none;
    box-shadow: none;
  }

  .naked:active {
    -webkit-transform: translateY(-1px);
    -ms-transform: translateY(-1px);
    transform: translateY(-1px);
    -webkit-box-shadow: none;
    box-shadow: none;
  }
}

/* variants */
.button {
  background: var(--ch-blue-100);
  border: 1px solid var(--ch-blue-100);
  color: var(--ch-white);
}

.link {
  text-decoration: underline;
}

.naked {
  background: none;
  color: inherit;
  width: auto;
  height: auto;
  padding: 0;
}

.invert {
  background: var(--ch-white);
  border: 1px solid var(--ch-white);
  color: var(--ch-blue-100);
}

.add {
  background: rgba(108, 189, 69, 0.15);
  border: 1px solid rgba(108, 189, 69, 0.15);
  color: var(--ch-black);
}

.create {
  background: rgba(108, 189, 69, 0.15);
  border: 1px solid rgba(108, 189, 69, 0.15);
  color: #418e1b;
}

.delete {
  background: rgba(230, 10, 12, 0.15);
  border: 1px solid rgba(230, 10, 12, 0.15);
  color: var(--ch-danger);
}

.edit {
  background: #e7ebf1;
  border: 1px solid #e7ebf1;
  color: var(--ch-blue-100);
}

.view {
  background: var(--ch-secondary-light);
  border: 1px solid var(--ch-secondary-light);
  color: var(--ch-secondary);
}

.outline {
  background: none;
  color: currentColor;
  border: 1px solid currentColor;
}

.deactivate {
  background: #eac6c6;
  border: 1px solid #eac6c6;
  color: var(--ch-inactive);
}

.warning {
  background: var(--ch-warning);
  border: 1px solid var(--ch-warning);
  color: var(--ch-white);
}

.danger {
  background: var(--ch-danger);
  border: 1px solid var(--ch-danger);
  color: var(--ch-white);
}

.stale {
  background: var(--ch-light-grey);
  border: 1px solid var(--ch-light-grey);
  color: var(--ch-dark-grey);
}

.cancel {
  border: 1px solid var(--ch-warning);
  color: var(--ch-warning) !important;
}

.naked:active {
  -webkit-transform: scale(0.99);
  -ms-transform: scale(0.99);
  transform: scale(0.99);
  opacity: 0.9;
  -webkit-transition: opacity 100ms;
  -o-transition: opacity 100ms;
  transition: opacity 100ms;
}

button.disabled,
button:hover.disabled,
button:active.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* size */
.xtra-small {
  font-size: var(--font-xs);
  font-weight: 400;
  padding: 0.25rem;
}
.small {
  font-size: var(--font-sm);
  font-weight: 400;
  padding: 0.5rem;
}

.large {
  font-size: 1.05rem;
  font-weight: 700;
  padding: 0.8rem;
}

.xtra-large {
  font-size: 1.2rem;
  font-weight: 700;
  padding: 0.85rem;
}

.xxtra-large {
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.02625rem;
  padding: 1rem 1.1rem;
}

/* rounded */
.rounded-xs {
  border-radius: 3px;
}

.rounded-sm {
  border-radius: 4px;
}

.rounded-lg {
  border-radius: 6px;
}

.rounded-xl {
  border-radius: 7px;
}

.rounded-xxl {
  border-radius: 10px;
}

.rounded-full {
  border-radius: 100%;
}
</style>
